import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';

import Loader from '@hpstellar/core/Loader';
import ProductTile from '../../product/components/product-tile';
import TopBanner from './top-banner';
import ResultsFooterBanner from './results-footer-banner';
import useProductPrice from '../../hooks/useProductPrice';
import useAddToCart from '../../hooks/useAddToCart';
import useCartState from '../../hooks/useCartState';
import useDeviceInfo from '../../hooks/useDeviceInfo';
import { ColorSelectorContainer } from '../../hooks/useColorSelector';
import useProductCompare from '../../hooks/useProductCompare';
import getProductProps from '../../product/get-product-props';
import InlineBanner from './inline-banner';
import { getPageUrl } from '../util';

import './vwa-product-list.less';

const DEFAULT_LAZYLOAD_SETTINGS = {
    robots: true,
    skip: true,
    offset: 800,
};

export default function VWAProductList({
    products,
    slugInfo,
    children,
    lazyLoadSettings = DEFAULT_LAZYLOAD_SETTINGS,
    firstProductRef,
    lastProductRef,
    history = {},
    loading,
    pageRefs,
    pageInView,
    setPageInView,
    infiniteScroll = false,
    filterCount,
    url,
    visId,
    tileContainerProps,
    useProductPromos,
    loadingList,
    maxResultsPerPage = 48
}) {
    let allProducts = infiniteScroll ? products.reduce((acc, group) => [...acc, ...group.results], []) : products;
    const { prices } = useProductPrice(!visId ? allProducts : [], { useSubscriptionsPromo: true, useProductPromos });

    const compare = useProductCompare(visId);
    const addToCart = useAddToCart(true);
    const { cartId } = useCartState();
    const { device } = useDeviceInfo(['device']);
    const groupObserver = useRef(null);

    const pageInViewAndNotLoadingList = pageInView && !loadingList;
    useEffect(() => {
        if (!infiniteScroll) return () => {};
        if (pageInViewAndNotLoadingList) history.push(getPageUrl(url, pageInView, filterCount > 0));
    }, [pageInViewAndNotLoadingList]);

    useEffect(() => {
        if (!infiniteScroll) return () => {};
        if (!loadingList && firstProductRef?.current) {
            firstProductRef.current.scrollIntoView(true);
        }
    }, [loadingList]);

    const groupRef = useCallback(
        node => {
            if (groupObserver.current) groupObserver.current.disconnect();
            groupObserver.current = new IntersectionObserver(
                entries => {
                    let items = {};
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            const value = entry.target.attributes['data-page']?.value;
                            if (!value) return;

                            if (items[value]) {
                                items[value] += 1;
                            } else {
                                items[value] = 1;
                            }
                        }
                    });

                    const key = Object.keys(items).reduce((acc, key) => {
                        if (items[key] > acc) {
                            return key;
                        }
                        return acc;
                    }, 0);

                    if (!key || key === '') return;
                    if (key !== pageInView) {
                        setPageInView(parseInt(key));
                    }
                },
                { threshold: 0.5 },
            );

            if (node?.children) {
                [...node.children].forEach(child => {
                    [...child.children].forEach(tile => {
                        groupObserver.current.observe(tile);
                    });
                });
            }
        },
        [products, pageInView],
    );

    const { analyticsData } = slugInfo || {};
    const { page_level, hsResID } = analyticsData || {};
    const hasResults = products.length > 0;
    const lazyLoadIndex = device === 'mobile' ? 1 : 5;
    const productListInfiniteScroll = infiniteScroll && loadingList ? [{ page: 1, results: Array(maxResultsPerPage).fill() }] : products;
    const productListWithoutInfiniteScroll = !infiniteScroll && loadingList ? Array(maxResultsPerPage).fill() : products;
    return (
        <div className={`vwaListContainer${visId ? ' visId' : ''}`}>
            {hasResults && <TopBanner />}
            <div className="vwaList" ref={groupRef}>
                {infiniteScroll &&
                    productListInfiniteScroll.map(({ page, results }) => {
                        return (
                            <div
                                id={`page${page}`}
                                className="vwaListContent"
                                data-url={getPageUrl(url, page, filterCount > 0, false)}
                                data-page={page}
                                key={page}
                            >
                                {results.map((product, i) => (
                                    <div
                                        key={i}
                                        ref={i === 0 ? pageRefs[page] : null}
                                        className={'refWrapper'}
                                        data-page={page}
                                    >
                                        <ColorSelectorContainer product={product}>
                                            {({ colorSelection }) => {
                                                let productProps = {};
                                                if(!loadingList) {
                                                    productProps = getProductProps(
                                                        product,
                                                        prices[product.sku],
                                                        {
                                                            addToCart,
                                                            compare,
                                                            colorSelection,
                                                        },
                                                        {
                                                            resizeImage: true,
                                                            hasPriceSeoText: true,
                                                        },
                                                        {
                                                            list: page_level,
                                                            cartId,
                                                            position: (product && product.position) || i + 1,
                                                            hsResID,
                                                        },
                                                    )
                                                }
                                                return (
                                                    <ProductTile
                                                        {...productProps}
                                                        forceShowPlaceholder={loadingList}
                                                        lazyLoadSettings={i > lazyLoadIndex && lazyLoadSettings}
                                                        className="productTile"
                                                        ratingSpaceReserved={true}
                                                        productRef={i === results.length - 1 ? lastProductRef : null}
                                                        visId={visId}
                                                        lazyLoad={i < lazyLoadIndex ? false : true}
                                                    />
                                                )
                                            }}
                                        </ColorSelectorContainer>
                                    </div>
                                ))}
                            </div>
                        );
                    })
                }
                {!infiniteScroll &&
                    productListWithoutInfiniteScroll.map((product, i) => {

                        const options = {
                            resizeImage: true,
                            hasPriceSeoText: true,
                        };
                        const analyticsData = {
                            list: page_level,
                            cartId,
                            position: (product && product.position) || i + 1,
                            hsResID,
                        };
                        let productProps = {};
                        
                        if(!loadingList){
                            productProps = visId
                            ? { product, options, analyticsData }
                            : getProductProps(
                                  product,
                                  prices[product.sku],
                                  {
                                      addToCart,
                                      compare,
                                  },
                                  {
                                      resizeImage: true,
                                      hasPriceSeoText: true,
                                  },
                                  {
                                      list: page_level,
                                      cartId,
                                      position: (product && product.position) || i + 1,
                                      hsResID,
                                  },
                              );
                              
                        }
                        return (
                            <ProductTile
                                key={i}
                                {...productProps}
                                forceShowPlaceholder={loadingList}
                                lazyLoadSettings={i > lazyLoadIndex && lazyLoadSettings}
                                className="productTile"
                                ratingSpaceReserved={true}
                                containerProps={tileContainerProps}
                                visId={visId}
                                lazyLoad={i < lazyLoadIndex ? false : true}
                            />
                        );
                    })}
                {hasResults && <InlineBanner />}
                {children}
            </div>
            {loading && <Loader className="infLoader" />}
            {hasResults && <ResultsFooterBanner />}
        </div>
    );
}
